/**
 * Хувь зээл — дэлгүүрийн өөрийн зээлтэй худалдан авагчид (2026-09-28).
 *
 * Гурван асуултад хариулна:
 *   1. Хэн хэдэн төгрөг өртэй, аль нь хугацаа хэтэрсэн бэ
 *   2. Тухайн зээлийн хуваарь, хэзээ хэдийг төлсөн бэ
 *   3. Сарын төлөлт бүртгэх, бүрэн төлөгдмөгц зээлийг хаах
 *
 * Зээл нь POS-ын борлуулалтаар үүснэ (эхний 10% + 90% авлага). Дүрэм,
 * хуваарь, үлдэгдэл серверт `@boots/domain` `installment.ts`-ээр тооцоологдоно.
 *
 * ⚠️ Салбарын ажилтан өөрийн салбарынхаа зээлийг л харна (сервер шүүнэ).
 * ⚠️ Төлөлт устгагдахгүй — алдаатайг менежер хүчингүй болгоно.
 * ⚠️ Зээл нээлттэй байх хооронд гүйлгээг буцаахгүй (сервер хориглоно).
 */

import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDate, formatDateTime, formatMnt, formatPhone, formatQty, moneyInput, PAYMENT_MN } from '@boots/domain';
import { api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Section } from '../components/section';
import { StatCard, StatGrid, StatPanel } from '../components/stat-card';
import {
  Button, Empty, ErrorNote, Field, Input, Modal, Select, Spinner, Table, Tabs, Td, Th,
} from '../components/ui';

interface Due {
  no: number;
  dueDate: string;
  amountMnt: string;
}

interface LoanPayment {
  id: string;
  method: string;
  amountMnt: string;
  paidAt: string;
  note: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  recordedBy: { fullName: string | null; username: string };
}

interface Loan {
  id: string;
  registryNo: string;
  downPaymentMnt: string;
  principalMnt: string;
  termMonths: number;
  openedAt: string;
  closedAt: string | null;
  store: { id: string; name: string };
  customer: { id: string; phone: string; name: string | null };
  order: { id: string; code: string; status: string; totalMnt: string; soldAt: string };
  payments: LoanPayment[];
  paidMnt: string;
  balanceMnt: string;
  overdueMnt: string;
  overdue: boolean;
  paidOff: boolean;
  open: boolean;
  nextDue: Due | null;
}

type LoanDetail = Loan & { schedule: Due[] };
type Status = 'open' | 'overdue' | 'closed' | 'all';

const PAY_METHODS = ['CASH', 'CARD', 'QPAY', 'TRANSFER'] as const;

export function InstallmentsPage() {
  const { hasPerm } = useAuth();
  const [status, setStatus] = useState<Status>('open');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const search = q.trim().length >= 2 ? q.trim() : '';

  // Картууд үргэлж НЭЭЛТТЭЙ зээлээс — таб солиход өөрчлөгдөхгүй
  const openLoans = useQuery({
    queryKey: ['installments', 'open', ''],
    queryFn: () => api<Envelope<Loan[]>>('/installments?status=open'),
  });
  const list = useQuery({
    queryKey: ['installments', status, search],
    queryFn: () =>
      api<Envelope<Loan[]>>(`/installments?status=${status}${search ? `&q=${encodeURIComponent(search)}` : ''}`),
    placeholderData: keepPreviousData,
  });

  if (openLoans.isPending) return <Spinner />;
  if (openLoans.error) return <ErrorNote>{(openLoans.error as Error).message}</ErrorNote>;

  const owing = openLoans.data.data;
  const totalLeft = owing.reduce((n, l) => n + Number(l.balanceMnt), 0);
  const overdue = owing.filter((l) => l.overdue);
  const nextDue = owing
    .filter((l) => l.nextDue)
    .sort((a, b) => (a.nextDue!.dueDate < b.nextDue!.dueDate ? -1 : 1))[0];

  return (
    <div className="flex flex-col gap-3">
      <StatPanel>
        <StatGrid>
          <StatCard
            label="Нийт авлага"
            icon="coins"
            value={formatMnt(totalLeft)}
            delta={{ text: `${formatQty(owing.length)} нээлттэй зээл`, good: totalLeft === 0 ? true : undefined }}
            info={<>Хувь зээлээр авсан, хараахан төлөгдөөгүй үлдэгдэл. Хүү, нэмэгдэл байхгүй.</>}
          />
          <StatCard
            label="Хугацаа хэтэрсэн"
            icon="percent"
            value={formatQty(overdue.length)}
            delta={{
              text: overdue.length
                ? `${formatMnt(overdue.reduce((n, l) => n + Number(l.overdueMnt), 0))} төлөгдөөгүй`
                : 'Хэтэрсэн зээл алга',
              good: overdue.length === 0,
            }}
            tone={overdue.length ? 'alert' : undefined}
          />
          <StatCard
            label="Хамгийн ойрын төлөлт"
            icon="receipt"
            value={nextDue?.nextDue ? formatDate(nextDue.nextDue.dueDate) : '—'}
            delta={{
              text: nextDue?.nextDue
                ? `${formatPhone(nextDue.customer.phone)} · ${formatMnt(nextDue.nextDue.amountMnt)}`
                : 'Төлөх зээл алга',
            }}
          />
        </StatGrid>
      </StatPanel>

      <Section
        title="Хувь зээл"
        actions={
          <Tabs<Status>
            value={status}
            onChange={setStatus}
            tabs={[
              { key: 'open', label: 'Нээлттэй' },
              { key: 'overdue', label: 'Хэтэрсэн' },
              { key: 'closed', label: 'Хаагдсан' },
              { key: 'all', label: 'Бүгд' },
            ]}
          />
        }
      >
        <div className="mb-3 max-w-xs">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Утас, регистр эсвэл баримт" />
        </div>
        {list.error ? <ErrorNote>{(list.error as Error).message}</ErrorNote> : null}
        {list.isPending ? <Spinner /> : <LoanTable rows={list.data?.data ?? []} onPick={setOpenId} />}
      </Section>

      {openId ? (
        <LoanModal
          id={openId}
          canPay={hasPerm('installments.pay')}
          canVoid={hasPerm('installments.void')}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </div>
  );
}

function LoanTable({ rows, onPick }: { rows: Loan[]; onPick: (id: string) => void }) {
  if (rows.length === 0) return <Empty>Энд зээл алга</Empty>;
  return (
    <Table>
      <thead>
        <tr>
          <Th>Баримт</Th>
          <Th>Худалдан авагч</Th>
          <Th>Салбар</Th>
          <Th num>Зээл</Th>
          <Th num>Төлсөн</Th>
          <Th num>Үлдэгдэл</Th>
          <Th>Дараагийн төлөлт</Th>
          <Th> </Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((l) => (
          <tr key={l.id} className="transition hover:bg-row-hover">
            <Td>
              <span className="num">{l.order.code}</span>
              <span className="block text-meta text-ink-3">
                {formatDate(l.openedAt)} · {formatQty(l.termMonths)} сар
              </span>
            </Td>
            <Td>
              <span className="num">{formatPhone(l.customer.phone)}</span>
              <span className="block text-meta text-ink-3">{l.registryNo}</span>
            </Td>
            <Td>{l.store.name}</Td>
            <Td num>{formatMnt(l.principalMnt)}</Td>
            <Td num>{formatMnt(l.paidMnt)}</Td>
            <Td num>
              <strong className={Number(l.balanceMnt) > 0 ? '' : 'text-ink-3'}>{formatMnt(l.balanceMnt)}</strong>
            </Td>
            <Td>
              {!l.open ? (
                <span className="text-ink-3">{l.order.status === 'VOIDED' ? 'Хүчингүй' : 'Хаагдсан'}</span>
              ) : l.nextDue ? (
                <span className={l.overdue ? 'text-alert-ink' : ''}>
                  {formatDate(l.nextDue.dueDate)} · {formatMnt(l.nextDue.amountMnt)}
                  {l.overdue ? ' · хэтэрсэн' : ''}
                </span>
              ) : (
                <span className="text-accent-700">Бүрэн төлөгдсөн — хаах</span>
              )}
            </Td>
            <td className="border-b border-hair/70 px-3 py-2 text-right">
              <button
                type="button"
                onClick={() => onPick(l.id)}
                className="inline-flex min-h-11 items-center rounded-full border border-hair
                  px-2.5 py-1 text-[11px] hover:bg-well sm:min-h-0"
              >
                Нээх
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** Нэг зээлийн хуваарь, төлөлтийн түүх, сарын төлөлт бүртгэх, хаах. */
function LoanModal({ id, canPay, canVoid, onClose }: {
  id: string;
  canPay: boolean;
  canVoid: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['installment', id],
    queryFn: () => api<Envelope<LoanDetail>>(`/installments/${id}`),
  });

  const refresh = async () => {
    setError(null);
    await qc.invalidateQueries({ queryKey: ['installment', id] });
    await qc.invalidateQueries({ queryKey: ['installments'] });
  };
  const onError = (e: unknown) => setError((e as Error).message);

  const close = useMutation({
    mutationFn: () => api(`/installments/${id}/close`, { method: 'POST' }),
    onSuccess: refresh,
    onError,
  });

  const l = detail.data?.data;

  return (
    <Modal
      title={l ? `${l.order.code} · ${formatPhone(l.customer.phone)}` : 'Хувь зээл'}
      subtitle={l ? `${l.registryNo} · ${l.store.name} · ${formatDate(l.openedAt)} · ${formatQty(l.termMonths)} сар` : ''}
      onClose={onClose}
      footer={<Button variant="ghost" onClick={onClose}>Хаах</Button>}
    >
      {detail.isPending ? <Spinner /> : null}
      {detail.error ? <ErrorNote>{(detail.error as Error).message}</ErrorNote> : null}
      {l ? (
        <div className="flex flex-col gap-3">
          <dl className="grid grid-cols-4 gap-3 rounded-card border border-hair px-3.5 py-3 text-[13px]">
            <Stat label="Зээл" value={formatMnt(l.principalMnt)} />
            <Stat label="Төлсөн" value={formatMnt(l.paidMnt)} />
            <Stat label="Үлдэгдэл" value={formatMnt(l.balanceMnt)} strong />
            <Stat label="Хэтэрсэн" value={l.overdue ? formatMnt(l.overdueMnt) : '—'} alert={l.overdue} />
          </dl>
          <p className="text-[11.5px] text-ink-3">
            Нийт {formatMnt(l.order.totalMnt)} · эхний төлөлт {formatMnt(l.downPaymentMnt)} ·{' '}
            {l.open ? 'зээл нээлттэй — гүйлгээг буцаахгүй' : l.order.status === 'VOIDED' ? 'захиалга хүчингүй' : 'зээл хаагдсан'}
          </p>

          {error ? <ErrorNote>{error}</ErrorNote> : null}

          <Schedule loan={l} />

          {canPay && l.open && !l.paidOff ? <PayForm loan={l} onDone={refresh} onError={onError} /> : null}
          {canPay && l.open && l.paidOff ? (
            <Button onClick={() => close.mutate()} disabled={close.isPending}>
              {close.isPending ? 'Хааж байна…' : 'Бүрэн төлөгдсөн — зээлийг хаах'}
            </Button>
          ) : null}

          <Payments loan={l} canVoid={canVoid} onDone={refresh} onError={onError} />
        </div>
      ) : null}
    </Modal>
  );
}

function Stat({ label, value, strong, alert }: { label: string; value: string; strong?: boolean; alert?: boolean }) {
  return (
    <div>
      <dt className="text-[11.5px] text-ink-3">{label}</dt>
      <dd className={`num ${strong ? 'font-semibold' : ''} ${alert ? 'text-alert-ink' : ''}`}>{value}</dd>
    </div>
  );
}

/** Сар бүрийн хуваарь — төлсөн дүнг хуваарийн дагуу хуримтлалаар тулгана. */
function Schedule({ loan }: { loan: LoanDetail }) {
  const paid = Number(loan.paidMnt);
  const today = new Date().toISOString().slice(0, 10);
  let cumulative = 0;
  return (
    <Table>
      <thead>
        <tr>
          <Th>Сар</Th>
          <Th>Төлөх өдөр</Th>
          <Th num>Дүн</Th>
          <Th>Байдал</Th>
        </tr>
      </thead>
      <tbody>
        {loan.schedule.map((d) => {
          cumulative += Number(d.amountMnt);
          const covered = paid >= cumulative;
          const late = !covered && d.dueDate < today && loan.open;
          return (
            <tr key={d.no}>
              <Td num>{d.no}</Td>
              <Td num>{formatDate(d.dueDate)}</Td>
              <Td num>{formatMnt(d.amountMnt)}</Td>
              <Td>
                <span className={covered ? 'text-accent-700' : late ? 'text-alert-ink' : 'text-ink-3'}>
                  {covered ? 'Төлсөн' : late ? 'Хэтэрсэн' : 'Хүлээгдэж буй'}
                </span>
              </Td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

function PayForm({ loan, onDone, onError }: {
  loan: LoanDetail;
  onDone: () => Promise<void>;
  onError: (e: unknown) => void;
}) {
  const [method, setMethod] = useState<string>('CASH');
  // Дараагийн сарын дүн, гэхдээ үлдэгдлээс ХЭТРЭХГҮЙ. Бутархай хэвээр — үнэ
  // нь бутархай гутлын үлдэгдэл 0.50₮ болж зээл хэзээ ч хаагдахгүй үлдэхээс сэргийлнэ.
  const [amount, setAmount] = useState(
    loan.nextDue ? String(Math.min(Number(loan.nextDue.amountMnt), Number(loan.balanceMnt))) : '',
  );
  const [note, setNote] = useState('');

  const add = useMutation({
    mutationFn: () =>
      api(`/installments/${loan.id}/payments`, {
        method: 'POST',
        body: { method, amountMnt: moneyInput(amount), ...(note.trim() ? { note: note.trim() } : {}) },
      }),
    onSuccess: async () => {
      setAmount('');
      setNote('');
      await onDone();
    },
    onError,
  });

  return (
    <div className="flex flex-col gap-2 rounded-card border border-hair px-3.5 py-3">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Хэлбэр">
          <Select
            value={method}
            onChange={setMethod}
            options={PAY_METHODS.map((m) => ({ value: m, label: PAYMENT_MN[m] ?? m }))}
          />
        </Field>
        <Field label="Дүн (₮)">
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
            inputMode="decimal"
            className="num"
            placeholder={String(Number(loan.balanceMnt))}
          />
        </Field>
      </div>
      <Field label="Тайлбар">
        <Input value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <Button onClick={() => add.mutate()} disabled={!amount || Number(amount) <= 0 || add.isPending}>
        {add.isPending ? 'Бүртгэж байна…' : 'Сарын төлөлт бүртгэх'}
      </Button>
      <p className="text-[11.5px] text-ink-3">
        ⚠️ Бүртгэсэн төлөлт устгагдахгүй — алдаатай бол менежер хүчингүй болгоно. Үлдэгдлээс их
        дүн бүртгэх боломжгүй.
      </p>
    </div>
  );
}

function Payments({ loan, canVoid, onDone, onError }: {
  loan: LoanDetail;
  canVoid: boolean;
  onDone: () => Promise<void>;
  onError: (e: unknown) => void;
}) {
  const [reason, setReason] = useState('');
  const voidOne = useMutation({
    mutationFn: (paymentId: string) =>
      api(`/installments/${loan.id}/payments/${paymentId}/void`, { method: 'POST', body: { reason: reason.trim() } }),
    onSuccess: async () => {
      setReason('');
      await onDone();
    },
    onError,
  });

  if (loan.payments.length === 0) return <Empty>Сарын төлөлт бүртгэгдээгүй</Empty>;
  const showVoid = canVoid && loan.open;

  return (
    <div className="flex flex-col gap-2">
      <Table>
        <thead>
          <tr>
            <Th>Хэзээ</Th>
            <Th>Хэлбэр</Th>
            <Th num>Дүн</Th>
            <Th>Бүртгэсэн</Th>
            <Th> </Th>
          </tr>
        </thead>
        <tbody>
          {loan.payments.map((p) => (
            <tr key={p.id} className={p.voidedAt ? 'text-ink-3 line-through' : undefined}>
              <Td num>{formatDateTime(p.paidAt)}</Td>
              <Td>{PAYMENT_MN[p.method] ?? p.method}</Td>
              <Td num>{formatMnt(p.amountMnt)}</Td>
              <Td>
                {p.recordedBy.fullName ?? p.recordedBy.username}
                {p.note ? <span className="block text-meta text-ink-2">{p.note}</span> : null}
                {p.voidReason ? <span className="block text-meta text-alert-ink">{p.voidReason}</span> : null}
              </Td>
              <Td>
                {showVoid && !p.voidedAt ? (
                  <button
                    type="button"
                    onClick={() => voidOne.mutate(p.id)}
                    disabled={voidOne.isPending || reason.trim().length < 3}
                    className="text-alert-ink hover:underline disabled:opacity-40"
                  >
                    Хүчингүй болгох
                  </button>
                ) : null}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {showVoid ? (
        <Field label="Хүчингүй болгох шалтгаан (дор хаяж 3 тэмдэгт)">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      ) : null}
    </div>
  );
}
